import {
  Modal,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
} from "@mui/material"
import Grid from "@mui/material/Grid2"
import { z } from "zod"
import { useState, useCallback } from "react"
import ClearIcon from "@mui/icons-material/Clear"
import { useCrudContext } from "@/context/crudContext"
import { useAddModel } from "@/hooks/useAddModel"
import useAttribute from "@/hooks/useAttribute"
import SingleSelect from "@/components/Select/SingleSelect"
import AddIcon from "@mui/icons-material/Add"
import AddAttributeNameModel from "../attribute-name/Model"
import { DevTool } from "@hookform/devtools"
import { useQueryClient } from "@tanstack/react-query"
const AttributeSchema = z.object({
  key: z.number(),
  attribute: z.string(),
  value: z.string().nullable(),
})

const AddAttributeModal = ({ open, onClose }) => {
  const [openAttributeNameModel, setOpenAttributeNameModel] = useState(false)
  const queryClient = useQueryClient()

  const {
    errors,
    isSubmitting,
    openDialog,
    selectedRow,
    register,
    handleSubmit,
    control,
    Controller,
    onSubmit,
    handleReset,
    defaultOnClose,
  } = useAddModel("attribute", AttributeSchema)
  const { attributes, isLoading: attributeLoading } = useAttribute()

  const handleAttributeNameClose = useCallback(() => {
    setOpenAttributeNameModel(false)
    queryClient.invalidateQueries("settings")
  }, [queryClient])

  return (
    <>
      <Modal
        open={open !== undefined ? open : openDialog}
        onClose={onClose || defaultOnClose}
      >
        <div className="w-[95vw] sm:w-[90vw] md:w-[600px] mx-auto mt-24 bg-white rounded-xl shadow-md p-4">
          {/* Modal Header */}
          <div className="flex items-center justify-between bg-[#f6f7f9] px-4 py-3 rounded-md">
            <h2 className="font-semibold text-black text-[16px] md:text-[18px]">
              {selectedRow ? "Update Attribute" : "Add New Attribute"}
            </h2>
            <IconButton
              aria-label="remove"
              size="small"
              onClick={onClose ? onClose : defaultOnClose}
            >
              <ClearIcon />
            </IconButton>
          </div>

          {/* Modal Body */}
          <form className="mt-4" onSubmit={handleSubmit(onSubmit)} noValidate>
            {/* Select Attribute Field + Add Button */}
            <div className="flex flex-row gap-2 mb-4">
              <div className="flex-grow">
                <SingleSelect
                  name="key"
                  controller={Controller}
                  control={control}
                  loading={attributeLoading}
                  label="Attribute"
                  options={attributes?.data}
                  error={!!errors.key}
                  helperText={errors.key?.message}
                  optionLabel="value"
                  optionValue="id"
                />
              </div>
              <Button
                variant="contained"
                onClick={() => setOpenAttributeNameModel(true)}
                sx={{
                  minWidth: "48px",
                  width: "48px",
                  height: "55px",
                  padding: 0,
                }}
              >
                <AddIcon />
              </Button>
            </div>

            {/* Value Input Field */}
            <div className="mb-4">
              <TextField
                fullWidth
                label="Value"
                error={!!errors.attribute}
                helperText={errors.attribute?.message}
                {...register("attribute")}
              />
            </div>

            <div className="mb-4">
              <TextField
                fullWidth
                label="Extra"
                error={!!errors.value}
                helperText={errors.value?.message}
                {...register("value")}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-end items-center gap-2 mt-4">
              <Button
                onClick={handleReset}
                variant="text"
                className="w-full sm:w-auto capitalize rounded-md font-medium text-[13px] py-3 px-6"
              >
                Reset
              </Button>
              <Button
                onClick={onClose ? onClose : defaultOnClose}
                variant="outlined"
                color="error"
                className="w-full sm:w-auto capitalize rounded-md font-medium text-[13px] py-3 px-6"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                variant="contained"
                className="w-full sm:w-auto capitalize rounded-md font-medium text-[13px] py-3 px-6 text-white"
              >
                {selectedRow ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      {openAttributeNameModel ? (
        <AddAttributeNameModel
          open={openAttributeNameModel}
          onClose={handleAttributeNameClose}
        />
      ) : null}
    </>
  )
}

export default AddAttributeModal
